/**
 * NOVAPAY-116-R5A — client helpers for prepare + sync handoff navigation.
 */
import { openHttpsSync } from "./external-link-v2.js";
import { markNovapayReturnExpected } from "./payment-lifecycle-v2.js";
import { emitEvent } from "./events-v2.js";
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";

export type HandoffPrepareResult = {
  ok: boolean;
  error?: string;
  order_id?: string;
  display_order_number?: string | null;
  payment_url?: string | null;
  handoff_url?: string | null;
  payment?: { payment_id?: string; status?: string } | null;
  message?: string | null;
};

const HANDOFF_URL = "/api/store-v2/novapay-handoff";

export async function prepareNovapayHandoff(input: {
  orderId: string;
  sessionKey: string;
  idempotencyKey?: string | null;
}): Promise<HandoffPrepareResult> {
  try {
    const res = await fetch(HANDOFF_URL, {
      method: "POST",
      headers: storeV2AuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({
        order_id: input.orderId,
        session_key: input.sessionKey,
        idempotency_key: input.idempotencyKey ?? `handoff:${input.orderId}:novapay`,
      }),
    });
    const json = (await res.json()) as HandoffPrepareResult;
    return json;
  } catch {
    return { ok: false, error: "network_error" };
  }
}

/** Prefer direct hosted URL; fall back to same-origin bridge redirect. */
export function openNovapayHandoffSync(input: {
  orderId: string;
  paymentUrl?: string | null;
  handoffUrl?: string | null;
}): { kind: "opened" | "failed"; via: string; url: string | null } {
  const url = String(input.paymentUrl || input.handoffUrl || "").trim();
  if (!url) return { kind: "failed", via: "missing_url", url: null };
  const result = openHttpsSync(url);
  if (result.kind === "opened") {
    markNovapayReturnExpected(input.orderId);
    emitEvent("payment_novapay_opened", {
      order_id: input.orderId,
      via: result.via,
      handoff: true,
    });
  }
  return { ...result, url };
}

export function buildHandoffGoUrl(orderId: string, sessionKey: string): string {
  const u = new URL("/api/store-v2/novapay-handoff", window.location.origin);
  u.searchParams.set("order_id", orderId);
  u.searchParams.set("session_key", sessionKey);
  return u.toString();
}
